import type { ExportFormat, ExportSource } from "@/hooks/use-search";

export type { ExportFormat, ExportSource };

/** A single rendered artifact — the multi-select step picks one or both,
 * which map onto the API's "pdf" | "csv" | "csv+pdf" format. */
export type ArtifactFormat = "pdf" | "csv";

export const ARTIFACT_FORMATS: ArtifactFormat[] = ["pdf", "csv"];

/** Mirrors the org privacy `candidate_name_display_mode` options — "none"
 * is the export-request spelling of "show_all". */
export type NameMode = "none" | "initials_only" | "anonymized";

export const NAME_MODES: {
  value: NameMode;
  label: string;
  description: string;
}[] = [
  {
    value: "none",
    label: "Show All Names",
    description: "Full candidate names (John Doe)",
  },
  {
    value: "initials_only",
    label: "Masked",
    description: "Initials only (J.D.)",
  },
  {
    value: "anonymized",
    label: "Anonymized",
    description: "Neutral labels (Candidate 4F2A91C0)",
  },
];

/** Strictness order — the org privacy policy is a floor: the export can be
 * stricter than the org setting, never looser. */
export const NAME_MODE_RANK: Record<NameMode, number> = {
  none: 0,
  initials_only: 1,
  anonymized: 2,
};

/** Candidate-data columns the CSV export can carry. Keys mirror the backend
 * CsvColumnKey enum; the defaults match its DEFAULT_CSV_COLUMNS. */
export type CsvColumnKey = NonNullable<
  import("@/lib/api/model").StartExportRequestSchema["csv_columns"]
>[number];

export const CSV_COLUMNS: {
  key: CsvColumnKey;
  label: string;
  isDefault: boolean;
}[] = [
  { key: "full_name", label: "Full name", isDefault: true },
  { key: "first_name", label: "First name", isDefault: true },
  { key: "last_name", label: "Last name", isDefault: true },
  { key: "headline", label: "Headline", isDefault: true },
  { key: "location", label: "Location", isDefault: true },
  { key: "linkedin_url", label: "Social URL", isDefault: true },
  { key: "current_title", label: "Current title", isDefault: true },
  { key: "current_company", label: "Current company", isDefault: true },
  { key: "summary", label: "Summary", isDefault: false },
  { key: "skills", label: "Skills", isDefault: false },
  { key: "education", label: "Education", isDefault: false },
  { key: "languages", label: "Languages", isDefault: false },
  { key: "certifications", label: "Certifications", isDefault: false },
];

export const DEFAULT_CSV_COLUMNS = CSV_COLUMNS.filter((c) => c.isDefault).map(
  (c) => c.key,
);

/** PDF colour theme; CSV-only exports send the default. */
export type ExportTheme = NonNullable<
  import("@/lib/api/model").StartExportRequestSchema["theme"]
>;

export const DEFAULT_EXPORT_THEME: ExportTheme = "dark";

export const EXPORT_THEMES: {
  value: ExportTheme;
  label: string;
  description: string;
}[] = [
  { value: "dark", label: "Dark", description: "Dark background" },
  {
    value: "light",
    label: "Light",
    description: "White background for printing and sharing",
  },
];

export function exportThemeLabel(theme: ExportTheme): string {
  return EXPORT_THEMES.find((t) => t.value === theme)?.label ?? "Dark";
}

/** The picked theme when a PDF is exported, otherwise the default. */
export function requestExportTheme(
  formats: ArtifactFormat[],
  theme: ExportTheme,
): ExportTheme {
  return formats.includes("pdf") ? theme : DEFAULT_EXPORT_THEME;
}

/** `unavailable`: no estimate to price from (failed, or answered without a
 * price), so the wizard prices it itself and says the cost is an estimate. */
export type EstimateState = "loading" | "ready" | "unavailable";

/** A failed refetch keeps the last response cached but clears `isSuccess`,
 * so a price the backend may have moved past is never `ready`. */
export function estimateStateOf(
  query: { isSuccess: boolean; isFetching: boolean },
  priced = true,
): EstimateState {
  if (query.isSuccess && priced) return "ready";
  return query.isFetching ? "loading" : "unavailable";
}

export interface ExportOptions {
  name_redaction: NameMode;
  hide_exporter: boolean;
  verified_contacts_only: boolean;
  personal_emails_only: boolean;
  personal_emails_preferred: boolean;
  include_highlights: boolean;
  csv_columns: CsvColumnKey[] | null;
  theme: ExportTheme;
}
